import React, { useEffect, useState } from 'react';
import { Shield, Copy } from 'lucide-react';
import Button from './ui/Button';
import Input from './ui/Input';
import { getCardEncryptionSettings, updateCardEncryptionSettings } from '../services/api';
import toast from 'react-hot-toast';

const failureMessage = (error) => {
  const status = error.response?.status;
  if (status === 403) return 'You do not have permission to manage card encryption.';
  if (status === 401) return 'Your session could not be verified. Please sign in again.';
  if (status >= 400 && status < 500) return error.response?.data?.message || 'Please check your encryption settings.';
  return 'Unable to save card encryption settings. Please try again.';
};

const CardEncryptionSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const [mode, setMode] = useState(null);
  const [publicKey, setPublicKey] = useState('');
  const [webhook, setWebhook] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError('');
    getCardEncryptionSettings().then(response => {
      if (!active) return;
      if (response.data?.status === false && Number(response.data?.code) === 404) {
        setSettings(null);
      } else if (response.data?.status === true) {
        setSettings(response.data.data);
      } else {
        setLoadError('Unable to load card encryption settings.');
      }
    }).catch(err => {
      if (!active) return;
      if (err.response?.status === 404) setSettings(null);
      else setLoadError(err.response?.status === 401 || err.response?.status === 403
        ? failureMessage(err) : 'Unable to load card encryption settings. Please try again.');
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [attempt]);

  const cancel = () => {
    setMode(null);
    setPublicKey('');
    setWebhook('');
    setError('');
  };

  const save = async (event) => {
    event.preventDefault();
    setError('');
    const payload = {};
    if ((!settings || mode === 'key') && publicKey.trim()) {
      if (/PRIVATE KEY/i.test(publicKey)) {
        setError('Never upload a private key. Paste only the contents of waya_public.pem.');
        return;
      }
      if (!publicKey.includes('-----BEGIN PUBLIC KEY-----') || !publicKey.includes('-----END PUBLIC KEY-----')) {
        setError('Paste a PEM public key including the BEGIN PUBLIC KEY and END PUBLIC KEY lines.');
        return;
      }
      payload.publicKey = publicKey;
    }
    if ((!settings || mode === 'webhook') && webhook.trim()) {
      try {
        const url = new URL(webhook.trim());
        if (url.protocol !== 'https:') throw new Error();
      } catch {
        setError('Enter a valid card webhook URL starting with https://.');
        return;
      }
      payload.cardWebhookUrl = webhook.trim();
    }
    if (!Object.keys(payload).length) {
      setError('Enter a public key or card webhook URL to save.');
      return;
    }
    if (settings && mode === 'key' && !window.confirm('Card details sent after this change can only be decrypted with the new private key. Make sure your systems are updated.')) return;
    setSaving(true);
    try {
      const response = await updateCardEncryptionSettings(payload);
      if (response.data?.status !== true) {
        setError(response.data?.message || 'Unable to save card encryption settings.');
        return;
      }
      setSettings(response.data.data);
      cancel();
      toast.success('Card encryption settings updated successfully');
    } catch (err) {
      // Do not log Axios errors: their request config contains the pasted key.
      setError(failureMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="border-t border-slate-100 pt-6 space-y-4" aria-labelledby="card-encryption-title">
      <h3 id="card-encryption-title" className="flex items-center gap-2 text-sm font-semibold text-slate-700">
        <Shield size={16} className="text-primary" /> Card Encryption
      </h3>
      <p className="text-sm text-slate-500">Card details we send you are encrypted with your public key. Only you can decrypt them. Keep your private key on your servers and never share it, including with Waya.</p>
      {loading ? <p role="status">Loading card encryption settings…</p> : loadError ? (
        <div className="space-y-3"><p role="alert" className="text-sm text-red-600">{loadError}</p><Button variant="secondary" onClick={() => setAttempt(value => value + 1)}>Retry</Button></div>
      ) : (
        <>
          {settings && (
            <div className="space-y-3 text-sm">
              <dl className="space-y-2">
                <div><dt className="font-medium">Active key ID</dt><dd className="flex items-center gap-2">
                  <span className="font-mono break-all">{settings.keyId ? `${settings.keyId.slice(0, 12)}…${settings.keyId.slice(-12)}` : 'No key registered'}</span>
                  {settings.keyId && <button type="button" aria-label="Copy key ID" onClick={async () => {
                    try { await navigator.clipboard.writeText(settings.keyId); toast.success('Key ID copied'); }
                    catch { toast.error('Unable to copy key ID'); }
                  }}><Copy size={16} /></button>}
                </dd></div>
                <div><dt className="font-medium">Algorithm</dt><dd>{settings.algorithm || '—'}</dd></div>
                <div><dt className="font-medium">Last updated</dt><dd>{settings.updatedAt || '—'}</dd></div>
                <div><dt className="font-medium">Card webhook URL</dt><dd className="break-all">{settings.cardWebhookUrl || 'Not configured'}</dd></div>
              </dl>
              {settings.publicKey && <details><summary className="cursor-pointer text-primary">View public key</summary><pre className="mt-2 p-3 bg-slate-50 rounded border whitespace-pre-wrap break-all text-xs">{settings.publicKey}</pre></details>}
              {!mode && <div className="flex flex-wrap gap-2">
                <Button variant="secondary" onClick={() => { setMode('webhook'); setWebhook(settings.cardWebhookUrl || ''); }}>Edit card webhook</Button>
                <Button variant="secondary" onClick={() => setMode('key')}>{settings.publicKey ? 'Replace key' : 'Upload public key'}</Button>
              </div>}
            </div>
          )}
          {(!settings || mode) && (
            <form onSubmit={save} className="space-y-4">
              {(!settings || mode === 'key') && <div>
                <label htmlFor="card-public-key" className="block text-sm font-medium text-slate-700 mb-1">Public key (PEM)</label>
                <textarea id="card-public-key" value={publicKey} onChange={event => setPublicKey(event.target.value)} placeholder="-----BEGIN PUBLIC KEY-----" rows={7} spellCheck={false} autoComplete="off" disabled={saving} className="w-full p-3 border border-slate-300 rounded-lg font-mono text-sm" />
              </div>}
              {(!settings || mode === 'webhook') && <Input id="card-webhook-url" label="Card webhook URL" type="url" placeholder="https://" value={webhook} onChange={event => setWebhook(event.target.value)} disabled={saving} helperText="Receives encrypted card details. This is separate from your general transaction webhook. Leaving this blank keeps the saved URL unchanged." />}
              {!settings && <p className="text-xs text-slate-500">Upload your public key first, or save it together with the card webhook. A webhook saved without a key cannot be displayed until a key is uploaded.</p>}
              {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
              <div className="flex justify-end gap-2">
                {settings && <Button type="button" variant="secondary" disabled={saving} onClick={cancel}>Cancel</Button>}
                <Button type="submit" isLoading={saving}>Save card encryption</Button>
              </div>
            </form>
          )}
        </>
      )}
      <details className="text-sm">
        <summary className="cursor-pointer text-primary">How do I generate a key?</summary>
        <div className="mt-3 space-y-3 text-slate-600">
          <p>Run these commands on a secure machine:</p>
          <pre className="overflow-x-auto p-3 bg-slate-50 rounded border text-xs">{'openssl genrsa -out waya_private.pem 2048\nopenssl rsa -in waya_private.pem -pubout -out waya_public.pem'}</pre>
          <p>Paste the contents of waya_public.pem here. Keep waya_private.pem safe on your servers. Never share it, including with Waya.</p>
          <p>If your key starts with -----BEGIN RSA PUBLIC KEY-----, convert it with:</p>
          <pre className="overflow-x-auto p-3 bg-slate-50 rounded border text-xs">openssl rsa -RSAPublicKey_in -in their_key.pem -pubout -out waya_public.pem</pre>
        </div>
      </details>
    </section>
  );
};

export default CardEncryptionSettings;
